import React, { useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import PuzzlePiece from './PuzzlePiece';
import PieceCropEditor from './PieceCropEditor';
import { useLanguage } from '../../contexts/LanguageContext';
import html2canvas from 'html2canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faFileDownload, faMagnet, faTrash } from '@fortawesome/free-solid-svg-icons';

const PuzzleWorkspace = ({ selectedPieces, setSelectedPieces }) => {
  const { t } = useLanguage();
  const [pdfAreas, setPdfAreas] = useState([]);
  const [editingPiece, setEditingPiece] = useState(null);
  const [magnetEnabled, setMagnetEnabled] = useState(true);
  const workspaceRef = useRef(null);

  // Debug logging
  console.log('PuzzleWorkspace selectedPieces:', selectedPieces);

  // Helper function to constrain area size to fit within workspace
  const constrainAreaSize = (area) => {
    if (!workspaceRef.current) return area;

    const workspaceRect = workspaceRef.current.getBoundingClientRect();
    const workspaceWidth = workspaceRect.width;
    const workspaceHeight = workspaceRect.height;

    console.log('Workspace dimensions:', { workspaceWidth, workspaceHeight });

    const areaWidth = area.width || 120;
    const areaHeight = area.height || 80;
    const areaScale = area.scale || 1;

    // Add padding to ensure areas never touch edges and controls remain accessible
    const padding = 40; // Extra space for mobile touch targets
    const availableWidth = Math.max(workspaceWidth - padding, 100);
    const availableHeight = Math.max(workspaceHeight - padding, 80);

    // Calculate what the maximum scale should be to fit in workspace with padding
    const maxScaleForWidth = availableWidth / areaWidth;
    const maxScaleForHeight = availableHeight / areaHeight;
    const maxScale = Math.min(maxScaleForWidth, maxScaleForHeight, 5); // Restore original max scale of 5

    // Ensure minimum scale for usability
    const minScale = 0.1;
    const constrainedScale = Math.max(minScale, Math.min(areaScale, maxScale));

    console.log('Size constraint calculation:', {
      workspaceSize: { workspaceWidth, workspaceHeight },
      availableSize: { availableWidth, availableHeight },
      areaSize: { areaWidth, areaHeight },
      scales: {
        requested: areaScale,
        maxForWidth: maxScaleForWidth,
        maxForHeight: maxScaleForHeight,
        final: constrainedScale
      }
    });

    return {
      ...area,
      scale: constrainedScale
    };
  };

  const [{ isOver }, drop] = useDrop({
    accept: 'area',
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();
      const dragOffset = monitor.getDifferenceFromInitialOffset();

      if (!workspaceRef.current || !offset) return;

      const workspaceRect = workspaceRef.current.getBoundingClientRect();

      // Calculate the drop position relative to the workspace
      let x = offset.x - workspaceRect.left;
      let y = offset.y - workspaceRect.top;

      // For preview areas, we'll adjust position after constraining size
      // Initial position will be recalculated below after we know the final scaled dimensions

      console.log('Drop position:', { x, y, offset, workspaceRect });

      if (item.fromWorkspace) {
        // Moving existing area in workspace - use the drag offset for smooth movement
        setPdfAreas(prev =>
          prev.map(area => {
            if (area.id === item.id) {
              // First constrain the area size
              const constrainedArea = constrainAreaSize(area);

              const proposedX = constrainedArea.x + (dragOffset?.x || 0);
              const proposedY = constrainedArea.y + (dragOffset?.y || 0);

              // Apply magnetic snapping
              const { x: newX, y: newY, hasSnapped } = applyMagneticSnap(constrainedArea, proposedX, proposedY);

              if (hasSnapped) {
                console.log('Area snapped during drag:', { areaId: item.id, from: { proposedX, proposedY }, to: { newX, newY } });
              }

              return { ...constrainedArea, x: newX, y: newY };
            }
            return area;
          })
        );
      } else {
        // Adding new area from selection
        const tempArea = {
          ...item,
          id: `workspace-${Date.now()}`,
          x: 0, // Temporary, will recalculate after constraining
          y: 0, // Temporary, will recalculate after constraining
          scale: item.scale || 1, // Ensure scale is set
          fromWorkspace: true // Mark as workspace area
        };

        // First constrain area size to fit within workspace
        const constrainedArea = constrainAreaSize(tempArea);

        // Now calculate the final scaled dimensions
        const finalScale = constrainedArea.scale || 1;
        const finalWidth = (constrainedArea.width || 120) * finalScale;
        const finalHeight = (constrainedArea.height || 80) * finalScale;

        // Center the piece at the drop position, accounting for the constrained size
        let finalX = x - finalWidth / 2;
        let finalY = y - finalHeight / 2;

        // Ensure area stays within workspace bounds
        finalX = Math.max(0, Math.min(finalX, workspaceRect.width - finalWidth));
        finalY = Math.max(0, Math.min(finalY, workspaceRect.height - finalHeight));

        // Update the constrained area with the final position
        constrainedArea.x = finalX;
        constrainedArea.y = finalY;

        // Apply magnetic snapping to new area with constrained size
        const { x: snappedX, y: snappedY, hasSnapped } = applyMagneticSnap(constrainedArea, finalX, finalY);

        let newArea = {
          ...constrainedArea,
          x: snappedX,
          y: snappedY
        };

        if (hasSnapped) {
          console.log('New area snapped on drop:', { from: { finalX, finalY }, to: { x: snappedX, y: snappedY } });
        }

        console.log('Adding new area with constrained size:', {
          originalSize: { width: item.width, height: item.height },
          finalSize: { width: finalWidth, height: finalHeight },
          scale: finalScale,
          position: { x: snappedX, y: snappedY }
        });
        setPdfAreas(prev => [...prev, newArea]);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  });

  const removeArea = (areaId) => {
    setPdfAreas(prev => prev.filter(area => area.id !== areaId));
  };

  const editPiece = (piece) => {
    setEditingPiece(piece);
  };

  const handleSaveCroppedPiece = (updatedPiece) => {
    // Update the piece in selectedPieces
    setSelectedPieces(prev => prev.map(p =>
      p.id === updatedPiece.id ? updatedPiece : p
    ));
    setEditingPiece(null);
  };

  const handleCancelCrop = () => {
    setEditingPiece(null);
  };

  const scaleArea = (areaId, scale) => {
    console.log('Scaling area in workspace:', { areaId, scale });
    setPdfAreas(prev =>
      prev.map(area => {
        if (area.id === areaId) {
          const updatedArea = { ...area, scale };
          const constrainedArea = constrainAreaSize(updatedArea);
          console.log('Updated area scale:', constrainedArea);
          return constrainedArea;
        }
        return area;
      })
    );
  };

  const updateAreaDimensions = (areaId, newWidth, newHeight) => {
    console.log('Updating area dimensions:', { areaId, newWidth, newHeight });
    setPdfAreas(prev =>
      prev.map(area => {
        if (area.id === areaId) {
          const updatedArea = {
            ...area,
            width: newWidth,
            height: newHeight
          };
          // Re-constrain the area with the new correct dimensions
          const reconstrainedArea = constrainAreaSize(updatedArea);
          console.log('Re-constrained area after dimension update:', {
            before: { width: area.width, height: area.height, scale: area.scale },
            after: { width: reconstrainedArea.width, height: reconstrainedArea.height, scale: reconstrainedArea.scale }
          });
          return reconstrainedArea;
        }
        return area;
      })
    );
  };

  const moveAreaTouch = (areaId, newX, newY) => {
    // Apply magnetic snapping for touch moves too
    const targetArea = pdfAreas.find(p => p.id === areaId);
    if (!targetArea) return;

    // First constrain the area size to ensure it fits
    const constrainedArea = constrainAreaSize(targetArea);

    // Ensure coordinates are within workspace bounds
    if (workspaceRef.current) {
      const workspaceRect = workspaceRef.current.getBoundingClientRect();
      const areaScale = constrainedArea.scale || 1;
      const areaWidth = (constrainedArea.width || 120) * areaScale;
      const areaHeight = (constrainedArea.height || 80) * areaScale;

      // Keep area within bounds with padding
      const padding = 10;
      newX = Math.max(padding, Math.min(newX, workspaceRect.width - areaWidth - padding));
      newY = Math.max(padding, Math.min(newY, workspaceRect.height - areaHeight - padding));
    }

    const { x: snappedX, y: snappedY, hasSnapped } = applyMagneticSnap(constrainedArea, newX, newY);

    if (hasSnapped) {
      console.log('Touch move with magnetic snap:', { areaId, from: { newX, newY }, to: { snappedX, snappedY } });
    }

    setPdfAreas(prev =>
      prev.map(area =>
        area.id === areaId
          ? { ...constrainedArea, x: snappedX, y: snappedY }
          : area
      )
    );
  };

  const handleTouchDrop = (area, x, y) => {
    console.log('Touch drop received:', { area: area.id, x, y });

    if (!workspaceRef.current) return;

    const workspaceRect = workspaceRef.current.getBoundingClientRect();

    // Create temporary area with default scale
    const tempArea = {
      ...area,
      id: `workspace-${Date.now()}`,
      x: 0, // Temporary, will recalculate after constraining
      y: 0, // Temporary, will recalculate after constraining
      scale: area.scale || 1, // Ensure scale is set
      fromWorkspace: true
    };

    // First, constrain the area size to fit within workspace
    const constrainedArea = constrainAreaSize(tempArea);

    // Now calculate the final scaled dimensions
    const finalScale = constrainedArea.scale || 1;
    const finalWidth = (constrainedArea.width || 120) * finalScale;
    const finalHeight = (constrainedArea.height || 80) * finalScale;

    // Center the piece at the drop position, accounting for the constrained size
    let finalX = x - finalWidth / 2;
    let finalY = y - finalHeight / 2;

    // Ensure area stays within workspace bounds
    finalX = Math.max(0, Math.min(finalX, workspaceRect.width - finalWidth));
    finalY = Math.max(0, Math.min(finalY, workspaceRect.height - finalHeight));

    // Update the constrained area with the final position
    constrainedArea.x = finalX;
    constrainedArea.y = finalY;

    // Apply magnetic snapping to the drop
    const { x: snappedX, y: snappedY, hasSnapped } = applyMagneticSnap(constrainedArea, finalX, finalY);

    const newArea = {
      ...constrainedArea,
      x: snappedX,
      y: snappedY
    };

    if (hasSnapped) {
      console.log('Touch drop with magnetic snap:', { from: { finalX, finalY }, to: { x: snappedX, y: snappedY } });
    }

    console.log('Touch drop: adding constrained area:', {
      originalSize: { width: area.width, height: area.height },
      finalSize: { width: finalWidth, height: finalHeight },
      scale: finalScale,
      position: { x: snappedX, y: snappedY }
    });
    setPdfAreas(prev => [...prev, newArea]);
  };

  // Magnetic snapping functionality
  const applyMagneticSnap = (draggedArea, newX, newY) => {
    // If magnet is disabled, return original position without snapping
    if (!magnetEnabled) {
      return { x: newX, y: newY, hasSnapped: false };
    }

    const snapDistance = 20; // pixels threshold for snapping
    const draggedScale = draggedArea.scale || 1;
    const draggedWidth = (draggedArea.width || 120) * draggedScale;
    const draggedHeight = (draggedArea.height || 80) * draggedScale;

    let snappedX = newX;
    let snappedY = newY;
    let hasSnapped = false;

    // Check against all other areas
    pdfAreas.forEach(otherArea => {
      if (otherArea.id === draggedArea.id) return; // Skip self

      const otherScale = otherArea.scale || 1;
      const otherWidth = (otherArea.width || 120) * otherScale;
      const otherHeight = (otherArea.height || 80) * otherScale;
      const otherX = otherArea.x;
      const otherY = otherArea.y;

      // Check for horizontal snapping (left/right alignment)
      // Snap to right edge of other area
      if (Math.abs(newX - (otherX + otherWidth)) < snapDistance &&
          Math.abs(newY - otherY) < snapDistance * 2) {
        snappedX = otherX + otherWidth;
        snappedY = otherY; // Also align vertically
        hasSnapped = true;
      }

      // Snap to left edge of other area
      else if (Math.abs((newX + draggedWidth) - otherX) < snapDistance &&
               Math.abs(newY - otherY) < snapDistance * 2) {
        snappedX = otherX - draggedWidth;
        snappedY = otherY; // Also align vertically
        hasSnapped = true;
      }

      // Check for vertical snapping (top/bottom alignment)
      // Snap to bottom edge of other area
      else if (Math.abs(newY - (otherY + otherHeight)) < snapDistance &&
               Math.abs(newX - otherX) < snapDistance * 2) {
        snappedY = otherY + otherHeight;
        snappedX = otherX; // Also align horizontally
        hasSnapped = true;
      }

      // Snap to top edge of other area
      else if (Math.abs((newY + draggedHeight) - otherY) < snapDistance &&
               Math.abs(newX - otherX) < snapDistance * 2) {
        snappedY = otherY - draggedHeight;
        snappedX = otherX; // Also align horizontally
        hasSnapped = true;
      }
    });

    if (hasSnapped) {
      console.log('Magnetic snap applied:', { originalPos: { newX, newY }, snappedPos: { snappedX, snappedY } });
    }

    return { x: snappedX, y: snappedY, hasSnapped };
  };

  const clearWorkspace = () => {
    setPdfAreas([]);
  };

  const exportAsImage = async () => {
    if (!workspaceRef.current || pdfAreas.length === 0) return;

    try {
      console.log('Starting high-resolution export...');

      // Get workspace dimensions
      const workspaceRect = workspaceRef.current.getBoundingClientRect();
      const workspaceWidth = workspaceRect.width;
      const workspaceHeight = workspaceRect.height;

      // Create ultra-high-resolution export canvas (8x the display resolution)
      const exportScale = 8;
      const exportCanvas = document.createElement('canvas');
      const exportContext = exportCanvas.getContext('2d');

      exportCanvas.width = workspaceWidth * exportScale;
      exportCanvas.height = workspaceHeight * exportScale;

      // Fill with white background
      exportContext.fillStyle = '#ffffff';
      exportContext.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

      console.log('Export canvas created:', {
        width: exportCanvas.width,
        height: exportCanvas.height,
        areas: pdfAreas.length
      });

      // Process each area at high resolution
      for (const area of pdfAreas) {
        try {
          console.log('Processing area for export:', {
            id: area.id,
            hasExtractedImage: !!area.extractedImage,
            imageType: area.extractedImage ? area.extractedImage.substring(0, 30) + '...' : 'none'
          });

          if (!area.extractedImage) {
            console.error('Area missing extractedImage:', area.id);
            continue; // Skip this area
          }

          // Convert the area's extractedImage back to an image
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = (error) => {
              console.error('Failed to load area image:', area.id, error);
              reject(error);
            };
            img.src = area.extractedImage;
          });

          // Calculate area dimensions and position for export
          const areaScale = area.scale || 1;
          const scaledWidth = (area.width || 120) * areaScale * exportScale;
          const scaledHeight = (area.height || 80) * areaScale * exportScale;
          const scaledX = area.x * exportScale;
          const scaledY = area.y * exportScale;

          console.log('Drawing area at high resolution:', {
            originalSize: { width: area.width, height: area.height },
            exportSize: { width: scaledWidth, height: scaledHeight },
            position: { x: scaledX, y: scaledY },
            scale: areaScale,
            exportScale
          });

          // Draw the area at high resolution
          exportContext.drawImage(
            img,
            scaledX, scaledY, scaledWidth, scaledHeight
          );

        } catch (areaError) {
          console.error('Error processing area for export:', area.id, areaError);
        }
      }

      console.log('All areas processed, creating download...');

      // Create download link with high-resolution image
      const link = document.createElement('a');
      link.download = `pdf-composition-${Date.now()}.png`;
      link.href = exportCanvas.toDataURL('image/png', 1.0); // Maximum quality
      link.click();

      console.log('High-resolution export completed successfully');

    } catch (error) {
      console.error('Error exporting high-resolution image:', error);
      alert('Error exporting image. Please try again.');
    }
  };

  drop(workspaceRef);

  return (
    <div className="puzzle-workspace">
      <div className="workspace-info">
        <p>{t('dragAreasHere')}</p>
        <p>{t('dragCornerToResize')}</p>
      </div>

      {selectedPieces.length > 0 ? (
        <div className="available-pieces">
          <h3>{t('availablePdfAreas')} ({selectedPieces.length})</h3>
          <div className="pieces-grid">
            {selectedPieces.map((area, index) => (
              <div key={area.id} style={{ position: 'relative' }}>
                <PuzzlePiece
                  piece={area}
                  onTouchDrop={handleTouchDrop}
                  isPreview={true}
                  fromWorkspace={false}
                />
                <button
                  onClick={() => editPiece(area)}
                  style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    padding: '0px',
                    width: '38px',
                    height: '38px',
                    elevation: 2,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    backgroundColor: "rgba(255,255,255,1)",
                    borderRadius: "18px",
                    //borderTopLeftRadius: "16px",
                    //borderBottomRightRadius: "16px",
                    //color: '#2ba5f7',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    zIndex: 5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon style={{color: '#2ba5f7'}} icon={faEdit} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="no-pieces">
          <p>{t('noAreasYet')}</p>
        </div>
      )}

      <div className="workspace-controls">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            //paddingTop: '10px',
            paddingLeft: '12px',
            flexDirection: 'column',
          }}
        >
          <div >
            <FontAwesomeIcon
            icon={faMagnet}
            style={{
              color: magnetEnabled ? '#2196F3' : '#999',
              fontSize: '16px',
              marginRight: '8px'
            }}
            />



          <label style={{
            position: 'relative',
            display: 'inline-block',
            width: '44px',
            height: '24px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={magnetEnabled}
              onChange={() => setMagnetEnabled(!magnetEnabled)}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
              position: 'absolute',
              cursor: 'pointer',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: magnetEnabled ? '#2196F3' : '#ccc',
              transition: '0.3s',
              borderRadius: '24px'
            }}>
              <span style={{
                position: 'absolute',
                content: '""',
                height: '18px',
                width: '18px',
                left: magnetEnabled ? '23px' : '3px',
                bottom: '3px',
                backgroundColor: 'white',
                transition: '0.3s',
                borderRadius: '50%',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }} />
            </span>
          </label>
        </div>
        <span
          className="control-btn-text"
          style={{
            marginTop: '-3px',
          }}
          >
            {t('magnetSnap')}
          </span>
        </div>
        <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          paddingRight: '12px',
          flexDirection: 'row',
        }}
        >
        <button
          onClick={clearWorkspace}
          disabled={pdfAreas.length === 0}
          className="control-btn"
        >
          <div className="control-btn-icon">
          <FontAwesomeIcon style={{color: "rgba(43,165,247,0.6)"}}icon={faTrash} />
          </div>
          {/* <span className="control-btn-icon">🗑️</span> */}
          <span className="control-btn-text">{t('clearAll')}</span>
        </button>
        <button
          onClick={exportAsImage}
          disabled={pdfAreas.length === 0}
          className="control-btn export-btn"
        >
          <div className="control-btn-icon">
          <FontAwesomeIcon icon={faFileDownload} />
          </div>
          {/* <span className="control-btn-icon">📥</span> */}
          <span className="control-btn-text">{t('exportImage')}</span>
        </button>
        </div>
      </div>

      <div
        ref={workspaceRef}
        className={`workspace-canvas ${isOver ? 'drop-over' : ''}`}
        style={{
          minHeight: '60vh',
          marginTop: '0.5rem',
          position: 'relative',
          border: '2px dashed #ccc',
          backgroundColor: isOver ? '#f0f8ff' : '#fafafa'
          // Removed touchAction: 'none' to allow page scrolling
        }}
      >
        {pdfAreas.length === 0 && !isOver && (
          <div className="empty-workspace">
            <p>{t('dropAreasHere')}</p>
          </div>
        )}

        {pdfAreas.map((area) => (
          <PuzzlePiece
            key={area.id}
            piece={area}
            onRemove={() => removeArea(area.id)}
            onScale={scaleArea}
            onTouchMove={moveAreaTouch}
            onUpdateDimensions={updateAreaDimensions}
            fromWorkspace={true}
          />
        ))}
      </div>



      {/* Crop Editor Modal */}
      {editingPiece && (
        <PieceCropEditor
          piece={editingPiece}
          onSave={handleSaveCroppedPiece}
          onCancel={handleCancelCrop}
        />
      )}
    </div>
  );
};

export default PuzzleWorkspace;
